import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ChevronLeft, Pencil, Check, X } from "lucide-react";
import { SpendingSummaryCards } from "../components/SpendingSummary";
import { ExpenseSourceSelector } from "../components/ExpenseSourceSelector";
import { MonthPicker } from "../components/MonthPicker";
import { PieChart } from "../charts/PieChart";
import { BarChart } from "../charts/BarChart";
import { SpendingInsights } from "../components/SpendingInsights";
import { categoryColor, subcategoryColor } from "../charts/colors";
import {
  categoryLabels,
  subcategoryLabels,
  formatSEK,
  formatDate,
} from "../lib/format";
import { useCategories } from "../hooks/useCategories";
import { isTransferTransaction } from "../types";
import type {
  Budget,
  ExpenseSource,
  SpendingSummary,
  MainCategory,
  Transaction,
  TransactionTypeFilter,
} from "../types";

interface DashboardPageProps {
  summary: SpendingSummary;
  transactions: Transaction[];
  budget: Budget | null;
  expenseSources: ExpenseSource[];
  selectedSourceIds: string[];
  defaultSourceIds: string[];
  allMonths: string[];
  selectedMonth: string | null;
  onMonthChange: (month: string | null) => void;
  onSourceChange: (sourceIds: string[]) => void;
  onUpdateCategory: (
    id: string,
    category: MainCategory,
    subcategory: string
  ) => Promise<void>;
  onOpenTransactions: (filter: TransactionTypeFilter) => void;
}

type DrillState =
  | { level: "categories" }
  | { level: "subcategories"; category: MainCategory }
  | { level: "transactions"; category: MainCategory; subcategory: string };

export function DashboardPage({
  summary,
  transactions,
  budget,
  expenseSources,
  selectedSourceIds,
  defaultSourceIds,
  allMonths,
  selectedMonth,
  onMonthChange,
  onSourceChange,
  onUpdateCategory,
  onOpenTransactions,
}: DashboardPageProps) {
  const { allCategories, getSubcategories, refresh: refreshCategories } = useCategories();
  const [drill, setDrill] = useState<DrillState>({ level: "categories" });
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editCat, setEditCat] = useState<string>("");
  const [editSub, setEditSub] = useState<string>("");
  const [customCat, setCustomCat] = useState("");
  const [customSub, setCustomSub] = useState("");

  const spendingTxs = transactions.filter(
    (t) => t.amount < 0 && t.includedInTotals && !isTransferTransaction(t)
  );

  function goBack() {
    if (drill.level === "transactions") {
      setDrill({ level: "subcategories", category: drill.category });
    } else if (drill.level === "subcategories") {
      setDrill({ level: "categories" });
    }
    setEditingId(null);
  }

  // Build pie data based on drill level
  let pieData: Array<{
    label: string;
    value: number;
    color: string;
    key: string;
  }> = [];
  let drillTransactions: Transaction[] = [];

  if (drill.level === "categories") {
    pieData = Object.entries(summary.byCategory)
      .filter(([_, value]) => (value as number) > 0)
      .map(([category, value]) => ({
        label: categoryLabels[category] ?? category,
        value: value as number,
        color: categoryColor(category),
        key: category,
      }))
      .sort((a, b) => b.value - a.value);
  } else if (drill.level === "subcategories") {
    pieData = Object.entries(summary.bySubcategory)
      .filter(
        ([key, value]) =>
          key.startsWith(`${drill.category}:`) && (value as number) > 0
      )
      .map(([key, value], i) => {
        const sub = key.split(":")[1];
        return {
          label: subcategoryLabels[sub] ?? sub,
          value: value as number,
          color: subcategoryColor(categoryColor(drill.category), i),
          key: sub,
        };
      })
      .sort((a, b) => b.value - a.value);
  } else {
    drillTransactions = spendingTxs
      .filter(
        (t) =>
          t.category === drill.category && t.subcategory === drill.subcategory
      )
      .sort(
        (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
      );
  }

  function handleSliceClick(key: string) {
    if (drill.level === "categories") {
      setDrill({ level: "subcategories", category: key as MainCategory });
    } else if (drill.level === "subcategories") {
      setDrill({
        level: "transactions",
        category: drill.category,
        subcategory: key,
      });
    }
  }

  function startEditing(tx: Transaction) {
    setEditingId(tx.id);
    setEditCat(tx.category);
    setEditSub(tx.subcategory);
    setCustomCat("");
    setCustomSub("");
  }

  async function confirmEdit(txId: string) {
    const finalCat = editCat === "__custom__" ? customCat.trim() : editCat;
    const finalSub =
      editCat === "__custom__" || editSub === "__custom__"
        ? customSub.trim()
        : editSub;

    if (finalCat && finalSub) {
      await onUpdateCategory(txId, finalCat as MainCategory, finalSub);
      await refreshCategories();
    }

    setEditingId(null);
  }

  // Build breadcrumb
  let titleParts: string[] = [];
  let showBackButton = false;
  let subtitle: string | null = null;

  if (drill.level === "categories") {
    titleParts = ["Utgifter per kategori"];
    subtitle = "Klicka på en kategori för att se underkategorier";
  } else if (drill.level === "subcategories") {
    titleParts = [categoryLabels[drill.category] ?? drill.category];
    subtitle = "Klicka på en underkategori för att se transaktioner";
    showBackButton = true;
  } else {
    titleParts = [
      categoryLabels[drill.category] ?? drill.category,
      subcategoryLabels[drill.subcategory] ?? drill.subcategory,
    ];
    showBackButton = true;
  }

  const barData = summary.byMonth.map((m) => ({
    label: m.month,
    spending: Math.round(m.spending * 100) / 100,
    income: Math.round(m.income * 100) / 100,
  }));

  const budgetTotal = budget
    ? Object.values(budget.categories).reduce((sum, v) => sum + v, 0)
    : 0;

  if (summary.transactionCount === 0 && summary.transferCount === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-muted-foreground">
        <p className="text-lg font-medium">Ingen data ännu</p>
        <p className="text-sm mt-1">
          Ladda upp kontoutdrag för att se din statistik
        </p>
      </div>
    );
  }

  const currentSubcats =
    editCat && editCat !== "__custom__"
      ? getSubcategories(editCat)
      : [];

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <h2 className="text-2xl font-bold tracking-tight">Utgifter</h2>
        <MonthPicker
          value={selectedMonth}
          availableMonths={allMonths}
          onChange={onMonthChange}
        />
      </div>

      <SpendingSummaryCards summary={summary} onCardClick={onOpenTransactions} />

      <ExpenseSourceSelector
        sources={expenseSources}
        selectedSourceIds={selectedSourceIds}
        defaultSourceIds={defaultSourceIds}
        onChange={onSourceChange}
      />

      <div className="grid md:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              {showBackButton && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7"
                  onClick={goBack}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
              )}
              {titleParts.map((part, i) => (
                <span key={i} className="flex items-center gap-1">
                  {i > 0 && (
                    <span className="text-muted-foreground font-normal">
                      /
                    </span>
                  )}
                  {part}
                </span>
              ))}
            </CardTitle>
            {subtitle && (
              <p className="text-xs text-muted-foreground">{subtitle}</p>
            )}
          </CardHeader>
          <CardContent>
            {drill.level === "transactions" ? (
              <div className="space-y-2">
                {drillTransactions.length === 0 ? (
                  <p className="text-sm text-muted-foreground py-4 text-center">
                    Inga transaktioner
                  </p>
                ) : (
                  <div className="max-h-[500px] overflow-y-auto">
                    <table className="w-full text-sm">
                      <thead className="sticky top-0 bg-card z-10">
                        <tr className="border-b text-left">
                          <th className="pb-2 font-medium text-muted-foreground">
                            Datum
                          </th>
                          <th className="pb-2 font-medium text-muted-foreground">
                            Beskrivning
                          </th>
                          <th className="pb-2 font-medium text-muted-foreground text-right">
                            Belopp
                          </th>
                          <th className="pb-2 font-medium text-muted-foreground w-8"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {drillTransactions.map((tx) => (
                          <tr
                            key={tx.id}
                            className="border-b border-border/50 group"
                          >
                            {editingId === tx.id ? (
                              <td colSpan={4} className="py-2">
                                <div className="space-y-2">
                                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                    <span className="tabular-nums">
                                      {formatDate(tx.date)}
                                    </span>
                                    <span className="truncate">
                                      {tx.description}
                                    </span>
                                    <span className="ml-auto font-medium text-destructive tabular-nums">
                                      {formatSEK(tx.amount)}
                                    </span>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <Select
                                      value={editCat}
                                      onValueChange={(v) => {
                                        setEditCat(v);
                                        setEditSub("");
                                        setCustomCat("");
                                        setCustomSub("");
                                      }}
                                    >
                                      <SelectTrigger className="h-8 w-[140px] text-xs">
                                        <SelectValue placeholder="Kategori" />
                                      </SelectTrigger>
                                      <SelectContent>
                                        {allCategories.map((cat) => (
                                          <SelectItem key={cat.value} value={cat.value}>
                                            {cat.label}
                                          </SelectItem>
                                        ))}
                                        <SelectItem value="__custom__">
                                          Skriv egen...
                                        </SelectItem>
                                      </SelectContent>
                                    </Select>

                                    {editCat === "__custom__" ? (
                                      <Input
                                        className="h-8 w-[140px] text-xs"
                                        placeholder="Ny kategori"
                                        value={customCat}
                                        onChange={(e) =>
                                          setCustomCat(e.target.value)
                                        }
                                        autoFocus
                                      />
                                    ) : null}

                                    {editCat && editCat !== "__custom__" ? (
                                      <Select
                                        value={editSub}
                                        onValueChange={(v) => {
                                          setEditSub(v);
                                          setCustomSub("");
                                        }}
                                      >
                                        <SelectTrigger className="h-8 w-[140px] text-xs">
                                          <SelectValue placeholder="Underkategori" />
                                        </SelectTrigger>
                                        <SelectContent>
                                          {currentSubcats.map((sub) => (
                                            <SelectItem key={sub.value} value={sub.value}>
                                              {sub.label}
                                            </SelectItem>
                                          ))}
                                          <SelectItem value="__custom__">
                                            Skriv egen...
                                          </SelectItem>
                                        </SelectContent>
                                      </Select>
                                    ) : null}

                                    {(editSub === "__custom__" ||
                                      editCat === "__custom__") && (
                                      <Input
                                        className="h-8 w-[140px] text-xs"
                                        placeholder="Ny underkategori"
                                        value={customSub}
                                        onChange={(e) =>
                                          setCustomSub(e.target.value)
                                        }
                                        autoFocus={editCat !== "__custom__"}
                                      />
                                    )}

                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      className="h-7 w-7 text-success"
                                      onClick={() => void confirmEdit(tx.id)}
                                      disabled={
                                        !(
                                          (editCat !== "__custom__" ||
                                            customCat.trim()) &&
                                          (editCat !== "__custom__" &&
                                          editSub !== "__custom__"
                                            ? editSub
                                            : customSub.trim()) &&
                                          (editCat !== "__custom__"
                                            ? editSub
                                            : true)
                                        )
                                      }
                                    >
                                      <Check className="h-4 w-4" />
                                    </Button>
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      className="h-7 w-7"
                                      onClick={() => setEditingId(null)}
                                    >
                                      <X className="h-4 w-4" />
                                    </Button>
                                  </div>
                                </div>
                              </td>
                            ) : (
                              <>
                                <td className="py-2 pr-3 tabular-nums whitespace-nowrap text-muted-foreground">
                                  {formatDate(tx.date)}
                                </td>
                                <td
                                  className="py-2 pr-3 truncate max-w-[200px]"
                                  title={tx.description}
                                >
                                  {tx.description}
                                </td>
                                <td className="py-2 pr-1 text-right tabular-nums whitespace-nowrap font-medium text-destructive">
                                  {formatSEK(tx.amount)}
                                </td>
                                <td className="py-2 w-8">
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity"
                                    onClick={() => startEditing(tx)}
                                  >
                                    <Pencil className="h-3 w-3" />
                                  </Button>
                                </td>
                              </>
                            )}
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr className="border-t-2">
                          <td
                            colSpan={3}
                            className="pt-2 font-medium text-sm"
                          >
                            Totalt ({drillTransactions.length} st)
                          </td>
                          <td className="pt-2 text-right tabular-nums font-bold text-destructive">
                            {formatSEK(
                              drillTransactions.reduce(
                                (s, t) => s + t.amount,
                                0
                              )
                            )}
                          </td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                )}
              </div>
            ) : (
              <PieChart data={pieData} onSliceClick={handleSliceClick} />
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Månadsvis</CardTitle>
          </CardHeader>
          <CardContent>
            <BarChart
              data={barData}
              budgetTotal={budgetTotal > 0 ? budgetTotal : undefined}
            />
          </CardContent>
        </Card>
      </div>

      <SpendingInsights
        selectedMonth={selectedMonth}
        selectedSourceIds={selectedSourceIds}
      />
    </div>
  );
}
